/**
 * CrDocumentModal — Add / Edit CR Document modal
 *
 * Schema facts (from compliance-cr.ts route):
 *  - documentType: "appointment" | "authority" | "training" | "compliance" | "termination" | "other"
 *  - documentStatus: "draft" | "active" | "superseded" | "archived"
 *  - filePath / fileUrl (NOT sharepointUrl)
 *  - effectiveDate / expiryDate
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CrDocumentData {
  id?: string;
  documentType?: string | null;
  documentName?: string | null;
  documentStatus?: string | null;
  fileUrl?: string | null;
  filePath?: string | null;
  effectiveDate?: string | null;
  expiryDate?: string | null;
}

interface CrDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditRepId: string;
  editingDocument?: CrDocumentData | null;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DOCUMENT_TYPES = [
  { value: "appointment", label: "Appointment" },
  { value: "authority", label: "Authority" },
  { value: "training", label: "Training" },
  { value: "compliance", label: "Compliance" },
  { value: "termination", label: "Termination" },
  { value: "other", label: "Other" },
];

const DOCUMENT_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "superseded", label: "Superseded" },
  { value: "archived", label: "Archived" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CrDocumentModal({
  open,
  onOpenChange,
  creditRepId,
  editingDocument,
  onSuccess,
}: CrDocumentModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingDocument?.id;

  // Form state
  const [documentType, setDocumentType] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [documentStatus, setDocumentStatus] = useState("draft");
  const [fileUrl, setFileUrl] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  // Reset / pre-populate form when modal opens
  useEffect(() => {
    if (open && editingDocument) {
      setDocumentType(editingDocument.documentType ?? "");
      setDocumentName(editingDocument.documentName ?? "");
      setDocumentStatus(editingDocument.documentStatus ?? "draft");
      setFileUrl(editingDocument.fileUrl ?? "");
      setEffectiveDate(editingDocument.effectiveDate ?? "");
      setExpiryDate(editingDocument.expiryDate ?? "");
    } else if (open && !editingDocument) {
      setDocumentType("");
      setDocumentName("");
      setDocumentStatus("draft");
      setFileUrl("");
      setEffectiveDate("");
      setExpiryDate("");
    }
  }, [open, editingDocument]);

  // Form validity
  const isValid = documentType.length > 0 && documentName.trim().length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/credit-reps/${creditRepId}/documents`,
        {
          documentType,
          documentName: documentName.trim(),
          documentStatus,
          fileUrl: fileUrl.trim() || undefined,
          effectiveDate: effectiveDate || undefined,
          expiryDate: expiryDate || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to add document");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}/documents`],
      });
      toast({ title: "Document added successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingDocument?.id) throw new Error("No document ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/cr-documents/${editingDocument.id}`,
        {
          documentType,
          documentName: documentName.trim(),
          documentStatus,
          fileUrl: fileUrl.trim() || undefined,
          effectiveDate: effectiveDate || undefined,
          expiryDate: expiryDate || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to update document");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/credit-reps/${creditRepId}/documents`],
      });
      toast({ title: "Document updated successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="modal-cr-document-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Document" : "Add Document"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the document details below."
              : "Add a document to this credit representative."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Document Type */}
          <div className="space-y-1.5">
            <Label>
              Document Type <span className="text-destructive">*</span>
            </Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="select-document-type">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Document Name */}
          <div className="space-y-1.5">
            <Label htmlFor="document-name">
              Document Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="document-name"
              data-testid="input-document-name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="e.g. Appointment Letter 2025"
            />
          </div>

          {/* Document Status */}
          <div className="space-y-1.5">
            <Label>Document Status</Label>
            <Select value={documentStatus} onValueChange={setDocumentStatus}>
              <SelectTrigger data-testid="select-document-status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File URL */}
          <div className="space-y-1.5">
            <Label htmlFor="file-url">File URL</Label>
            <Input
              id="file-url"
              data-testid="input-file-url"
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="effective-date">Effective Date</Label>
              <Input
                id="effective-date"
                data-testid="input-effective-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expiry-date">Expiry Date</Label>
              <Input
                id="expiry-date"
                data-testid="input-expiry-date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Add Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
